import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  FunnelIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { blobPreviewUrl } from '../../utils/blobPreviewUrl'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'

const INCIDENTS_HELP_SCREEN_KEY = 'page:compliance-safe/incidents'

const TYPE_LABELS = {
  third_party: 'Third-party collection',
  structuring: 'Structuring / smurfing',
  minimal_play: 'Minimal play / cash conversion',
  suspicious: 'Suspicious behaviour',
  counterfeit_fraud: 'Counterfeit / fraud',
  self_exclusion: 'Self-exclusion breach',
  intoxication: 'Intoxication',
  underage: 'Underage',
  other: 'Other concern',
  no_incident: 'No incident to report',
}

const SEVERITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
}

/** Activity reports: Open → Under Investigation | Recorded. Nil attestations: Recorded (auto-closed). */
const WORKFLOW_STATUS_COLORS = {
  open: 'bg-blue-50 text-blue-700',
  under_investigation: 'bg-red-50 text-red-700',
  recorded: 'bg-emerald-50 text-emerald-700',
}

const WORKFLOW_STATUS_LABELS = {
  open: 'Open',
  under_investigation: 'Under Investigation',
  recorded: 'Recorded',
}

function isNoIncident(inc) {
  return String(inc?.incident_type || '').trim() === 'no_incident'
}

function linkedCaseFinished(inc) {
  const s = String(inc?.linked_case_status || '').trim().toUpperCase()
  return s === 'CLOSED' || s === 'RESOLVED' || s === 'FILED'
}

function incidentWorkflowStatus(inc) {
  if (!inc) return 'open'
  if (isNoIncident(inc) || inc.status === 'closed') return 'recorded'
  if (linkedCaseFinished(inc)) return 'recorded'
  if (inc.linked_case_id) return 'under_investigation'
  return 'open'
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatIncidentWhen(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return String(value)
  }
}

/** Open a print-ready Unusual Activity Report (UAR) document. */
function printIncidentUar(inc) {
  if (!inc) return
  const typeLabel = TYPE_LABELS[inc.incident_type] || inc.incident_type || '—'
  const workflow = incidentWorkflowStatus(inc)
  const statusLabel = WORKFLOW_STATUS_LABELS[workflow] || workflow
  const submittedBy = isNoIncident(inc) ? nilSubmitterLabel(inc) : (inc.reported_by || '—')
  const memberLabel = inc.visitor_id && !inc.member_id ? 'Visitor / Entry ID' : 'Member'
  const memberValue = inc.member_id || inc.visitor_id || '—'
  const concern = inc.incident_option_label || inc.incidentOptionLabel || ''
  const staffRemark = inc.staff_remark || inc.staffRemark || ''
  const photos = scenePhotosFromAttachments(inc.attachments).filter((p) => p.kind === 'image')
  const photoHtml = photos.length
    ? photos
        .map(
          (p, i) =>
            `<figure class="photo"><figcaption>Scene photo ${p.index != null ? p.index + 1 : i + 1}</figcaption><img src="${escapeHtml(p.src)}" alt="Scene photo" /></figure>`
        )
        .join('')
    : '<p class="muted">No scene photos attached.</p>'

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>UAR ${escapeHtml(inc.id || '')}</title>
  <style>
    @page { margin: 16mm; }
    * { box-sizing: border-box; }
    body { font-family: Georgia, "Times New Roman", serif; color: #1a1a1a; margin: 0; padding: 24px; line-height: 1.45; }
    .brand { font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase; color: #0d7377; font-family: system-ui, sans-serif; font-weight: 700; }
    h1 { font-size: 22px; margin: 6px 0 4px; font-weight: 700; }
    .sub { color: #555; font-size: 13px; margin-bottom: 18px; font-family: system-ui, sans-serif; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; border: 1px solid #d8d8d8; border-radius: 8px; padding: 14px 16px; margin-bottom: 18px; font-family: system-ui, sans-serif; font-size: 13px; }
    .meta dt { color: #666; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; margin: 0; }
    .meta dd { margin: 2px 0 0; font-weight: 600; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; background: #ecfdf5; color: #047857; }
    section { margin-bottom: 16px; page-break-inside: avoid; }
    section h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; color: #0d7377; font-family: system-ui, sans-serif; margin: 0 0 6px; }
    section p { margin: 0; white-space: pre-wrap; font-size: 14px; }
    .photo { margin: 10px 0; page-break-inside: avoid; }
    .photo img { max-width: 100%; max-height: 280px; border: 1px solid #ddd; border-radius: 6px; }
    .photo figcaption { font-size: 11px; color: #666; margin-bottom: 4px; font-family: system-ui, sans-serif; }
    .muted { color: #777; font-size: 13px; font-family: system-ui, sans-serif; }
    .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #ddd; font-size: 11px; color: #777; font-family: system-ui, sans-serif; }
    @media print { body { padding: 0; } .no-print { display: none !important; } }
  </style>
</head>
<body>
  <div class="brand">PaySafeGo · Unusual Activity Report</div>
  <h1>${escapeHtml(typeLabel)}</h1>
  <p class="sub">Report ID <strong>${escapeHtml(inc.id || '—')}</strong> · Status <span class="badge">${escapeHtml(statusLabel)}</span></p>
  <dl class="meta">
    <div><dt>Venue</dt><dd>${escapeHtml(inc.venue_id || '—')}</dd></div>
    <div><dt>Severity</dt><dd>${escapeHtml(inc.severity || '—')}</dd></div>
    <div><dt>${escapeHtml(isNoIncident(inc) ? 'Submitted by' : 'Reported by')}</dt><dd>${escapeHtml(submittedBy)}</dd></div>
    <div><dt>Reported at</dt><dd>${escapeHtml(formatIncidentWhen(inc.created_at))}</dd></div>
    <div><dt>${escapeHtml(memberLabel)}</dt><dd>${escapeHtml(memberValue)}</dd></div>
    <div><dt>Location</dt><dd>${escapeHtml(inc.location_detail || '—')}</dd></div>
    ${inc.linked_case_id ? `<div><dt>Linked investigation</dt><dd>${escapeHtml(inc.linked_case_id)}</dd></div>` : ''}
    ${inc.reviewed_by ? `<div><dt>Reviewed by</dt><dd>${escapeHtml(inc.reviewed_by)}</dd></div>` : ''}
  </dl>
  ${concern ? `<section><h2>Selected concern</h2><p>${escapeHtml(concern)}</p></section>` : ''}
  <section><h2>Description</h2><p>${escapeHtml(inc.description || '—')}</p></section>
  ${staffRemark ? `<section><h2>Staff remark</h2><p>${escapeHtml(staffRemark)}</p></section>` : ''}
  ${inc.review_notes ? `<section><h2>Review notes</h2><p>${escapeHtml(inc.review_notes)}</p></section>` : ''}
  <section><h2>Scene photos</h2>${photoHtml}</section>
  <p class="footer">Printed ${escapeHtml(new Date().toLocaleString())} from PaySafeGo Incident Monitoring.</p>
</body>
</html>`

  // Print via a same-origin hidden iframe (no popup blocker).
  const FRAME_ID = 'psg-uar-print-frame'
  let frame = document.getElementById(FRAME_ID)
  if (frame) frame.remove()
  frame = document.createElement('iframe')
  frame.id = FRAME_ID
  frame.title = 'UAR print'
  frame.setAttribute('aria-hidden', 'true')
  Object.assign(frame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  })
  document.body.appendChild(frame)

  const doc = frame.contentDocument || frame.contentWindow?.document
  if (!doc) {
    toast.error('Could not prepare the print view')
    frame.remove()
    return
  }
  doc.open()
  doc.write(html)
  doc.close()

  const runPrint = () => {
    try {
      frame.contentWindow?.focus()
      frame.contentWindow?.print()
    } catch (err) {
      toast.error(err?.message || 'Print failed')
    } finally {
      // Keep frame briefly so the print dialog can capture content, then clean up.
      window.setTimeout(() => {
        try {
          frame.remove()
        } catch (_) {
          /* ignore */
        }
      }, 1000)
    }
  }

  // Wait for images (and layout) before printing when photos are present.
  const images = Array.from(doc.images || [])
  if (images.length === 0) {
    window.setTimeout(runPrint, 50)
    return
  }
  let pending = images.length
  let printed = false
  const maybePrint = () => {
    if (printed) return
    pending -= 1
    if (pending <= 0) {
      printed = true
      window.setTimeout(runPrint, 50)
    }
  }
  images.forEach((img) => {
    if (img.complete) maybePrint()
    else {
      img.addEventListener('load', maybePrint, { once: true })
      img.addEventListener('error', maybePrint, { once: true })
    }
  })
  window.setTimeout(() => {
    if (!printed) {
      printed = true
      runPrint()
    }
  }, 2500)
}

function nilSubmitterLabel(inc) {
  if (!inc) return '—'
  const name = inc.attested_staff_name || inc.attestedStaffName
  const eid = inc.attested_employee_id || inc.attestedEmployeeId
  if (name && eid) return `${name} (${eid})`
  if (name) return name
  if (eid) return eid
  return inc.reported_by || inc.reportedBy || '—'
}

function investigationPath(linkedCaseId) {
  if (!linkedCaseId) return null
  return `/compliance-safe/investigations/${encodeURIComponent(linkedCaseId)}`
}

/** Mobile POST stores scene photos in `attachments` (jsonb): base64 inline or blob `{ type, url }` after upload. */
function parseIncidentAttachments(raw) {
  if (raw == null) return []
  if (typeof raw === 'object' && raw.type === 'Buffer' && Array.isArray(raw.data)) {
    try {
      const text = new TextDecoder().decode(new Uint8Array(raw.data))
      return parseIncidentAttachments(text)
    } catch {
      return []
    }
  }
  if (Array.isArray(raw)) return raw
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw)
      return Array.isArray(p) ? p : []
    } catch {
      return []
    }
  }
  return []
}

function scenePhotosFromAttachments(attachments) {
  return parseIncidentAttachments(attachments)
    .filter((x) => x && x.type === 'scene_photo')
    .map((att, idx) => {
      if (att.missing) return { kind: 'missing', index: att.photo_index ?? idx }
      const src = blobPreviewUrl(att, att.content_type || 'image/jpeg')
      return src ? { kind: 'image', src, index: att.photo_index ?? idx } : { kind: 'none', index: idx }
    })
}

function hasScenePhoto(attachments) {
  const photos = scenePhotosFromAttachments(attachments)
  return photos.some((p) => p.kind === 'image' || p.kind === 'none')
}

function incidentHasMissingPhotoFlag(attachments) {
  return parseIncidentAttachments(attachments).some((x) => x?.type === 'scene_photo' && x.missing)
}

/** List API may send `has_scene_photo` instead of attachment payloads. */
function incidentListHasPhoto(inc) {
  if (inc?.has_scene_photo === true) return true
  return hasScenePhoto(inc?.attachments)
}

const PAGE_SIZE = 50

/** Scene photo via signed proxy URL — show placeholder if the image fails to load. */
function ScenePhotoImage({ src, alt }) {
  const [failed, setFailed] = React.useState(false)
  React.useEffect(() => {
    setFailed(false)
  }, [src])
  if (failed) {
    return (
      <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2">
        Could not load this photo. Try <strong>Refresh</strong> on the list, then open the incident again.
      </p>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      className="max-h-64 w-full rounded-md object-contain bg-gray-100"
      onError={() => setFailed(true)}
    />
  )
}

function StatusBadge({ inc }) {
  const workflow = incidentWorkflowStatus(inc)
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${WORKFLOW_STATUS_COLORS[workflow]}`}
    >
      {WORKFLOW_STATUS_LABELS[workflow]}
    </span>
  )
}

export default function IncidentMonitoring() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [incidents, setIncidents] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [filter, setFilter] = useState({ status: '', incident_type: '', dateFrom: '', dateTo: '', member_id: '' })
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [counts, setCounts] = useState({ total: 0, open: 0, under_investigation: 0, recorded: 0 })
  const [reviewNotes, setReviewNotes] = useState('')
  const [updating, setUpdating] = useState(false)
  const [creatingCase, setCreatingCase] = useState(false)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)

  useEffect(() => {
    const memberFromUrl = searchParams.get('member_id')
    if (memberFromUrl) {
      setFilter((prev) => ({ ...prev, member_id: memberFromUrl }))
      setPage(1)
    }
  }, [searchParams])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (page - 1) * PAGE_SIZE
      let url = `/api/mobile/incidents?limit=${PAGE_SIZE}&offset=${offset}`
      if (filter.member_id) url += `&member_id=${encodeURIComponent(filter.member_id)}`
      if (filter.status) url += `&status=${encodeURIComponent(filter.status)}`
      if (filter.incident_type) url += `&incident_type=${filter.incident_type}`
      if (filter.dateFrom) url += `&dateFrom=${encodeURIComponent(filter.dateFrom)}`
      if (filter.dateTo) url += `&dateTo=${encodeURIComponent(filter.dateTo)}`
      const res = await authFetch(getLocalApiUrl(url))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setIncidents(Array.isArray(data.data) ? data.data : [])
      setTotal(Number(data.total) || 0)
      if (data.counts && typeof data.counts === 'object') {
        const recordedFromServer = Number(data.counts.recorded) || 0
        const dismissedLegacy = Number(data.counts.dismissed) || 0
        setCounts({
          total: Number(data.counts.total) || 0,
          open: Number(data.counts.open) || 0,
          under_investigation: Number(data.counts.under_investigation) || 0,
          // New API folds closed activity into recorded; older APIs still send dismissed separately.
          recorded: data.counts.recorded_includes_closed
            ? recordedFromServer
            : recordedFromServer + dismissedLegacy,
        })
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load incidents')
    } finally {
      setLoading(false)
    }
  }, [filter, page])

  useEffect(() => { load() }, [load])

  const updateFilter = (patch) => {
    setFilter((f) => ({ ...f, ...patch }))
    setPage(1)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rangeFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const rangeTo = Math.min(page * PAGE_SIZE, total)

  const openIncidentDetail = async (inc) => {
    setSelected(inc)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/incidents/${encodeURIComponent(inc.id)}`))
      const data = await res.json().catch(() => ({}))
      const detail = data.data
      const detailHasFields = detail && (detail.id || detail.description || detail.incident_type)
      if (res.ok && detailHasFields) {
        const merged = {
          ...inc,
          ...detail,
          attachments:
            detail.attachments != null && parseIncidentAttachments(detail.attachments).length > 0
              ? detail.attachments
              : inc.attachments,
        }
        setSelected(merged)
      }
    } catch (_) {
      /* keep list row */
    }
  }

  const recordIncident = async (id) => {
    setUpdating(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/mobile/incidents/${encodeURIComponent(id)}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'closed',
          review_notes: reviewNotes.trim() || 'Recorded — no further action required',
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Failed to record incident')
      toast.success('Incident recorded')
      setSelected(null)
      setReviewNotes('')
      await load()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setUpdating(false)
    }
  }

  const startInvestigation = async (inc) => {
    const invPath = investigationPath(inc.linked_case_id)
    if (invPath) {
      navigate(invPath)
      return
    }
    setCreatingCase(true)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/mobile/incidents/${encodeURIComponent(inc.id)}/create-case`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to start investigation')
      toast.success(data.message || 'Investigation created')
      setSelected(null)
      setReviewNotes('')
      await load()
      const caseRef = data.data?.case_number || data.data?.id
      if (caseRef) navigate(investigationPath(caseRef))
    } catch (e) {
      toast.error(e.message)
    } finally {
      setCreatingCase(false)
    }
  }

  const selectedWorkflow = selected ? incidentWorkflowStatus(selected) : null

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-red-500 p-3 shadow-lg">
            <ExclamationTriangleIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">UAR Reports</h1>
            <p className="text-sm text-gray-600">AML unusual activity reports from floor mobile devices</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HelpScreenButton
            screenKey={INCIDENTS_HELP_SCREEN_KEY}
            onClick={() => setHelpPanelOpen(true)}
          />
          <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-gray-500 uppercase">Total</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{counts.total}</p>
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 shadow-sm">
          <p className="text-xs font-medium text-blue-600 uppercase">Open</p>
          <p className="mt-1 text-2xl font-bold text-blue-700">{counts.open}</p>
        </div>
        <div className="rounded-xl border border-red-100 bg-red-50 p-4 shadow-sm">
          <p className="text-xs font-medium text-red-600 uppercase">Under Investigation</p>
          <p className="mt-1 text-2xl font-bold text-red-700">{counts.under_investigation}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 shadow-sm">
          <p className="text-xs font-medium text-emerald-600 uppercase">Recorded</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700">{counts.recorded}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <FunnelIcon className="h-4 w-4 text-gray-400" />
        <select value={filter.status} onChange={e => updateFilter({ status: e.target.value })}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="under_investigation">Under Investigation</option>
          <option value="recorded">Recorded</option>
        </select>
        <select value={filter.incident_type} onChange={e => updateFilter({ incident_type: e.target.value })}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input
          type="date"
          value={filter.dateFrom}
          onChange={e => updateFilter({ dateFrom: e.target.value })}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          title="From date"
        />
        <span className="text-xs text-gray-500">to</span>
        <input
          type="date"
          value={filter.dateTo}
          onChange={e => updateFilter({ dateTo: e.target.value })}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          title="To date"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">ID</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Severity</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Venue</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Submitted by</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Photo</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              )}
              {!loading && incidents.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No incidents found</td></tr>
              )}
              {!loading && incidents.map(inc => (
                <tr key={inc.id} className="hover:bg-gray-50/80 cursor-pointer" onClick={() => openIncidentDetail(inc)}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{inc.id}</td>
                  <td className="px-4 py-3">{TYPE_LABELS[inc.incident_type] || inc.incident_type}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${SEVERITY_COLORS[inc.severity] || ''}`}>
                      {inc.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{inc.venue_id}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {isNoIncident(inc) ? nilSubmitterLabel(inc) : inc.reported_by}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge inc={inc} />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {incidentListHasPhoto(inc) ? (
                      <span className="font-medium text-psg-teal">Yes</span>
                    ) : incidentHasMissingPhotoFlag(inc.attachments) ? (
                      <span className="text-amber-600" title="Reporter flagged a photo but no image was sent">Flagged</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {inc.created_at ? new Date(inc.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={e => { e.stopPropagation(); openIncidentDetail(inc) }}
                      className="text-psg-teal hover:underline text-xs font-medium">
                      <EyeIcon className="h-4 w-4 inline" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
            <p>
              Showing {rangeFrom}–{rangeTo} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl p-6 space-y-4 mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">{TYPE_LABELS[selected.incident_type] || selected.incident_type}</h2>
                <p className="text-xs text-gray-500 font-mono">{selected.id}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => printIncidentUar(selected)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  title="Print Unusual Activity Report"
                >
                  <PrinterIcon className="h-4 w-4" />
                  Print UAR
                </button>
                <button type="button" onClick={() => setSelected(null)} aria-label="Close">
                  <XMarkIcon className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500">Venue:</span> <span className="font-medium">{selected.venue_id}</span></div>
              <div><span className="text-gray-500">Severity:</span> <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_COLORS[selected.severity]}`}>{selected.severity}</span></div>
              <div>
                <span className="text-gray-500">{isNoIncident(selected) ? 'Submitted by:' : 'Reported by:'}</span>{' '}
                <span className="font-medium">
                  {isNoIncident(selected) ? nilSubmitterLabel(selected) : selected.reported_by}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>{' '}
                <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${WORKFLOW_STATUS_COLORS[selectedWorkflow]}`}>
                  {WORKFLOW_STATUS_LABELS[selectedWorkflow]}
                </span>
              </div>
              {(selected.member_id || selected.visitor_id) && (
                <div>
                  <span className="text-gray-500">
                    {selected.visitor_id && !selected.member_id ? 'Visitor / Entry ID:' : 'Member:'}
                  </span>{' '}
                  <span className="font-medium">{selected.member_id || selected.visitor_id}</span>
                </div>
              )}
              {selected.location_detail && <div><span className="text-gray-500">Location:</span> <span className="font-medium">{selected.location_detail}</span></div>}
              <div className="col-span-2"><span className="text-gray-500">Time:</span> <span className="font-medium">{selected.created_at ? new Date(selected.created_at).toLocaleString() : '—'}</span></div>
            </div>

            {(selected.incident_option_label || selected.incidentOptionLabel) && (
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs font-semibold text-gray-500 mb-1">Selected concern</p>
                <p className="text-sm text-gray-800">
                  {selected.incident_option_label || selected.incidentOptionLabel}
                </p>
              </div>
            )}

            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{selected.description}</p>
            </div>

            {(selected.staff_remark || selected.staffRemark) && (
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs font-semibold text-gray-500 mb-1">Staff remark</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                  {selected.staff_remark || selected.staffRemark}
                </p>
              </div>
            )}

            {(() => {
              const photos = scenePhotosFromAttachments(selected.attachments)
              const images = photos.filter((p) => p.kind === 'image')
              const unavailable = photos.filter((p) => p.kind === 'none')
              const missing = photos.some((p) => p.kind === 'missing')
              if (images.length === 0 && unavailable.length === 0 && !missing) return null
              return (
                <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                  <p className="text-xs font-semibold text-gray-500">
                    Scene photo{images.length > 1 ? 's' : ''} (from mobile)
                  </p>
                  {missing && images.length === 0 && (
                    <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2">
                      A photo was indicated but no image data was received.
                    </p>
                  )}
                  {unavailable.map((photo, i) => (
                    <p
                      key={`${selected.id}-photo-unavail-${photo.index}-${i}`}
                      className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-2"
                    >
                      Photo {(photo.index ?? i) + 1} is on file but could not be loaded. Try Refresh, then open again.
                    </p>
                  ))}
                  {images.map((photo, i) => (
                    <div key={`${selected.id}-photo-${photo.index}-${i}`} className="space-y-2">
                      {images.length + unavailable.length > 1 && (
                        <p className="text-xs text-gray-500">Photo {photo.index + 1}</p>
                      )}
                      <a
                        href={photo.src}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-psg-teal hover:underline"
                      >
                        Open / save image
                      </a>
                      <ScenePhotoImage src={photo.src} alt={`Incident scene ${photo.index + 1}`} />
                    </div>
                  ))}
                </div>
              )
            })()}

            {selected.review_notes && (
              <div className="rounded-lg bg-amber-50 p-3">
                <p className="text-xs font-semibold text-amber-600 mb-1">Review notes ({selected.reviewed_by})</p>
                <p className="text-sm text-amber-800">{selected.review_notes}</p>
              </div>
            )}

            {selectedWorkflow === 'recorded' && isNoIncident(selected) && (
              <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                Nil attestation — automatically recorded. No investigation required.
              </p>
            )}

            {selectedWorkflow === 'open' && !isNoIncident(selected) && (
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-xs text-gray-500">
                  <strong>Investigate</strong> creates a compliance investigation and links it here.
                  <strong> Record</strong> closes the report with no investigation (status becomes Recorded).
                </p>
                <textarea
                  placeholder="Notes (optional)"
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => startInvestigation(selected)}
                    disabled={creatingCase || updating}
                    className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {creatingCase ? 'Creating…' : 'Investigate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => recordIncident(selected.id)}
                    disabled={updating || creatingCase}
                    className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-900 disabled:opacity-50"
                  >
                    {updating ? 'Recording…' : 'Record'}
                  </button>
                </div>
              </div>
            )}

            {selectedWorkflow === 'under_investigation' && selected.linked_case_id && (
              <div className="rounded-lg border border-red-100 bg-red-50/60 p-4 space-y-2">
                <p className="text-xs font-semibold text-red-800 uppercase tracking-wide">Linked investigation</p>
                <p className="text-sm text-gray-700">
                  This incident is in the compliance investigation pipeline.
                </p>
                <Link
                  to={investigationPath(selected.linked_case_id)}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-psg-teal hover:underline"
                >
                  {selected.linked_case_id} →
                </Link>
              </div>
            )}

            {selectedWorkflow === 'recorded' && !isNoIncident(selected) && (
              <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg p-3 space-y-2">
                <p className="m-0">
                  {selected.linked_case_id
                    ? `Investigation ${selected.linked_case_id} is complete. This UAR is recorded.`
                    : 'This incident was recorded with no further investigation.'}
                </p>
                {selected.linked_case_id ? (
                  <Link
                    to={investigationPath(selected.linked_case_id)}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-psg-teal hover:underline"
                  >
                    {selected.linked_case_id} →
                  </Link>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
      <ScreenHelpPanel
        screenKey={INCIDENTS_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="incidents-help"
      />
    </div>
  )
}
